import { FC, useMemo } from "react"
import { DLMDOC } from "../../_models"
import { DetailCard, DetailItem, DetailFileDownload, DetailLabelKeysToCells, extractDOCDetailFiles } from "../../../../../components/layout"
import { fieldChanged, showDate } from "../../../../../components/helpers"
import { PageViewLink } from "../../../../../components/widgets"
import { ApplicationAttachment } from "../../../applications"


const DLM_DOC_FIELDS = {
  cbRef: 'DOC Reference No',
  companyName: 'CoC Holder',
  installer: 'Installer',
  projectName: 'Project',
  planNo: 'SCDF Building Plan No',
  otherRef: 'Other Reference No',
}

const DLM_DOC_APP_DETAILS = {
  productCategoryString: 'Product Type (as per CoC)',
  cocNumber: "COC No.",
  productBrandString: 'Brand'
}

type Props = {
  dlmAppId: number
  details: DLMDOC & {
    // some Application details
    applicationType?: string
    disclaimerAck?: boolean
    docSplitArea?: boolean
    comment?: string
    otherAttachments?: Array<ApplicationAttachment>
    docs?: Array<DLMDOC>
  }
  cardTitle?: string
  highlightChanges?: boolean
  canReplace?: boolean
}

const DOCDetails: FC<Props> = ({dlmAppId, details, cardTitle = "Details", highlightChanges, canReplace}) => {

  const showFiles = useMemo(() => {
    if (details?.docs && details.docs.length > 0) {
      return extractDOCDetailFiles(details.docs, dlmAppId);
    }
    return extractDOCDetailFiles([details], dlmAppId)
  }, [dlmAppId, details]);

  return (
  <DetailCard cardTitle={cardTitle}>
    
    {DetailLabelKeysToCells(DLM_DOC_FIELDS, details, false, highlightChanges)}

    <div className='separator border-gray-200 mb-2'></div>
    {DetailLabelKeysToCells(DLM_DOC_APP_DETAILS, details.applicationCertificate, false, highlightChanges)}
    <DetailItem labelText="Model" content={`${details.model}`} hasChanged={highlightChanges && fieldChanged(details, 'model')} />
    { (details.hasOwnProperty('areaOfCoverage')) &&
    <DetailItem labelText={`${details?.applicationType === "TYPE_DOCINCREASE" ? 'NEW ' : ''} Total Area of Coverage`} content={`${details.areaOfCoverage} ${details.unit}`} hasChanged={highlightChanges && fieldChanged(details, 'areaOfCoverage')} />
    }
    { (details?.docSplitArea) &&
    <DetailItem labelText="Requested Split Area" content={`${details.docSplitArea}`} hasChanged={highlightChanges && fieldChanged(details, 'docSplitArea')} />
    }
    { details?.hourRating &&
    <DetailItem labelText="Fire Resistance Rating (Hr)" content={`${details.hourRating}`} hasChanged={highlightChanges && fieldChanged(details, 'hourRating')} />
    }
    { details?.location &&
    <DetailItem labelText="Location" content={`${details.location}`} hasChanged={highlightChanges && fieldChanged(details, 'location')} />
    }
    { (details?.fromDOC && details?.fromDOC?.id > 0) &&
    <>
    <div className='separator border-gray-200 mb-2'></div>
    <DetailItem labelText="From DOC" hasChanged={highlightChanges && fieldChanged(details, 'fromDOC')}>
        {details.fromDOC.cbRef}
        &nbsp;
        <PageViewLink id={details.fromDOC.id} pageType="dlmDOC">(view)</PageViewLink>
    </DetailItem>
    </>
    }
    { (showFiles && showFiles.length > 0) &&
    <>
    <div className='separator border-gray-200 mb-2'></div>
    {showFiles.map((doc: any, idx: number) => (
    <DetailFileDownload key={`showfile${idx}`} labelText={doc.showFileTitle} file={doc.showFile} setscoOnly={false} hasChanged={highlightChanges && doc.hasChanged} data={doc} canReplace={canReplace}/>
    ))}
    </>
    }
    { (details?.issueDate) &&
    <DetailItem labelText="DOC Issue Date" hasChanged={highlightChanges && fieldChanged(details, 'issueDate')} >
      {showDate(details.issueDate, 'dateOnly')}
    </DetailItem>
    }
    <div className='separator border-gray-200 mb-2' />
    {
    (details?.comment) &&
    <DetailItem labelText="Comment" content={details.comment} hasChanged={highlightChanges && fieldChanged(details, 'comment')} />
    }
    { (details?.disclaimerAck) &&
    <DetailItem labelText="Disclaimer Acknowledged" >
      <div className='form-check form-check-sm form-check-custom form-check-solid'>
        <input className='form-check-input pe-none' type='checkbox' checked readOnly />
      </div>
    </DetailItem>
    }
    {
    (details?.otherAttachments && details.otherAttachments.length > 0) &&
    <DetailItem labelText="Other Attachments" hasChanged={highlightChanges && fieldChanged(details, 'otherAttachments')}>
      <div className="mh-150px overflow-auto pe-6 me-n4">
        { //@ts-ignore
        details.otherAttachments.map((file: any, idx: number) => (
          <DetailFileDownload key={`otherFile${idx}`} labelText={`File ${idx + 1}`} file={file}/>
        ))}
      </div>
    </DetailItem>
    }
  </DetailCard>
  )
}

export { DOCDetails }